/**
 * Page keys: how a page is named in a user category's list of pages it may open.
 *
 * A page is named by its menu path without the leading slash (`/pred_casa` is `pred_casa`). The
 * menu in `config/menuConfig.ts` is the one list of pages; the backend only checks that a key is
 * well formed, so adding a menu entry needs no change there.
 *
 * Imports from `menuVisibility.ts` are type-only: that module builds on this one.
 */

import type { MenuVisibilityFields } from './menuVisibility';

/**
 * Routes that open a menu page under another address. The router matches a route's address
 * without regard to case, so the guard cannot match on the exact path either: without the alias
 * `/contracte` would open Contracts for a user who may not.
 */
const ROUTE_PAGE_ALIASES: Readonly<Record<string, string>> = { contracte: 'contracts' };

/** The page key of a menu path: `/pred_casa` is `pred_casa`. */
export function pageKeyOf(path: string): string {
    return path.replace(/^\/+/, '');
}

/**
 * The page key an address opens: its first segment, lower-cased, with the alias applied.
 * `/contracts/add` is `contracts`, `/Pred_Casa` is `pred_casa`, `/` is the empty string.
 */
export function routePageKey(pathname: string): string {
    const path = pageKeyOf(pathname);
    const separator = path.indexOf('/');
    const key = (separator === -1 ? path : path.slice(0, separator)).toLowerCase();

    return ROUTE_PAGE_ALIASES[key] ?? key;
}

/**
 * True when the administrator can grant or take away the entry: it is shown in the menu and is not
 * reserved for administrators. An entry switched off in configuration, or one only reachable from
 * another page (Clients, from Contracts), is not a page in its own right.
 */
export function isPermissionable(item: MenuVisibilityFields): boolean {
    return item.visible && item.enabled && item.adminOnly !== true;
}

/** The entries whose access the administrator decides, in menu order. */
export function permissionableItems<T extends MenuVisibilityFields>(items: readonly T[]): T[] {
    return items.filter(isPermissionable);
}

/**
 * True when `pageKey` may be opened. `null` means no restriction; a list is exactly the pages that
 * may be opened; `undefined` means the answer has not arrived and allows nothing.
 */
export function isPageAllowed(
    allowedPages: readonly string[] | null | undefined,
    pageKey: string,
): boolean {
    return allowedPages === null || (allowedPages?.includes(pageKey) ?? false);
}

/**
 * The text of a menu entry: its translation, or - when no translation exists and i18next hands
 * back the key itself - the name the entry carries.
 */
export function resolveMenuLabel(
    translated: string,
    translationKey: string,
    fallbackName: string,
): string {
    return translated === translationKey ? fallbackName : translated;
}

/** The translation key of a menu entry's label. */
export function menuLabelKey(path: string): string {
    return `menu.${pageKeyOf(path).toLowerCase()}`;
}
